import { DayEnum } from '@wonderschool/common-base-types';
import {
  BusinessCoachCard,
  Button,
  ButtonLink,
  CurrencyInput,
  Divider,
  Input,
  Label,
  LinkOutIcon,
  Option,
  RadioButton,
  Select,
  Switch,
} from '@wonderschool/common-base-ui';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import utc from 'dayjs/plugin/utc';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../../../Components/Shared/PageHeader';
import { convertToCents } from '../../../../helpers/money';
import { capitalizeFirstLetter } from '../../../../helpers/utils';
import { NUMBERS_OF_THE_MONTH_OPTIONS } from '../../../consts';
import {
  calculateEndBillingDate,
  calculateProrateAmount,
  calculateRecurringInvoiceTotal,
  calculateStartBillingDate,
  convertScheduledDaysToWeekdayNumbers,
  getNextInvoices,
  shouldCalculateProrateAmount,
} from '../../../enrollmentsUtils';
import { BillingIntervalEnum, WEEKDAYS } from '../../../enums';
import { InvitationRecipientType } from '../../../types';
import EditProrateAmountModal from '../../modals/EditProrateAmountModal';
import EnrollmentOneTimeInvoicesForm from '../EnrollmentOneTimeInvoicesForm';
import RecurringFeesForm from '../RecurringFeesForm';

dayjs.extend(utc);
dayjs.extend(isSameOrBefore);

const HELP_ARTICLE_URL =
  'https://wonderschool.zendesk.com/hc/en-us/articles/32108790060951-Setting-up-recurring-plan-and-one-time-invoices-during-the-enrollment-process';

type StudentInvoiceProps = {
  enrollmentForm: any;
  studentFromForm: any;
  invitationRecipients: InvitationRecipientType[];
  scheduledDays: any[];
};

const StudentInvoice: React.FC<StudentInvoiceProps> = ({ enrollmentForm, invitationRecipients, scheduledDays }) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fixedProrateAmount, setFixedProrateAmount] = useState<number | null>(null);

  // Initialize parentEmail when invitationRecipients are available
  useEffect(() => {
    if (invitationRecipients.length > 0 && !enrollmentForm.getValues('parentEmail')) {
      const firstParentEmail = invitationRecipients[0]?.email;
      if (firstParentEmail) {
        enrollmentForm.setValue('parentEmail', firstParentEmail);
      }
    }
  }, [invitationRecipients, enrollmentForm]);

  const billingIntervalOptions = useMemo(
    () => [
      { label: t('enrollments.billingWeekly'), value: BillingIntervalEnum.WEEKLY, name: t('week') },
      { label: t('enrollments.billingMonthly'), value: BillingIntervalEnum.MONTHLY, name: t('month') },
    ],
    [t]
  );

  const billingPeriodOptions = {
    weekly: [
      { label: t('enrollments.priorWeek'), value: 'Prior' },
      { label: t('enrollments.nextWeek'), value: 'Next' },
    ],
    'bi-weekly': [
      { label: t('enrollments.priorTwoWeeks'), value: 'Prior' },
      { label: t('enrollments.nextTwoWeeks'), value: 'Next' },
    ],
    monthly: [
      { label: t('enrollments.priorMonth'), value: 'Prior' },
      { label: t('enrollments.nextMonth'), value: 'Next' },
    ],
  };

  const weekDaysOptions = useMemo(
    () => WEEKDAYS.map((day) => ({ label: t(capitalizeFirstLetter(day)), value: day })),
    [t]
  );

  const biweeklyOptions = useMemo(
    () =>
      WEEKDAYS.reduce((arr: Option[], day: DayEnum) => {
        return [
          ...arr,
          { label: t(`enrollments.first${capitalizeFirstLetter(day)}`), value: `1-${day}` },
          { label: t(`enrollments.second${capitalizeFirstLetter(day)}`), value: `2-${day}` },
        ];
      }, []),
    [t]
  );

  const chargeDateOptions = {
    weekly: weekDaysOptions,
    'bi-weekly': biweeklyOptions,
    monthly: NUMBERS_OF_THE_MONTH_OPTIONS,
  };

  const discountOptions = [
    { text: t('common.credit'), value: 'Credit' },
    { text: t('billing.dropdown.EmployeeDiscount'), value: 'Employee Discount' },
    { text: t('billing.dropdown.FinancialAid'), value: 'Financial Aid' },
    { text: t('enrollments.scholarship'), value: 'Scholarship' },
    { text: t('common.revenueSubsidy'), value: 'Subsidy' },
    { text: t('common.referral'), value: 'Referral' },
    { text: t('billing.dropdown.Sibling/FamilyDiscount'), value: 'Sibling/Family Discount' },
  ];

  const feeOptions = [
    { text: t('revenueDiaperFee'), value: 'Diaper Fee' },
    { text: t('billing.dropdown.MealFee'), value: 'Meal Fee' },
    { text: t('enrollments.materialFee'), value: 'Material Fee' },
  ];

  const [feeCategoryOptions, setFeeCategoryOptions] = useState({
    Discount: discountOptions,
    Fee: feeOptions,
  });

  const findSelectedOption = useCallback(
    (arr: Option[], value: string) => arr?.find((option) => option.value === value),
    []
  );

  const findSelectedOptionName = useCallback(
    (value: string) => billingIntervalOptions?.find((option) => option.value === value)?.name,
    [billingIntervalOptions]
  );

  const selectedBillingInterval = useWatch({ control: enrollmentForm.control, name: 'billingInterval' }) || '';
  const intervalBillingPeriodOptions = billingPeriodOptions[selectedBillingInterval] || [];
  const intervalChargeDatePeriodOptions = chargeDateOptions[selectedBillingInterval] || [];
  const chargeDate = useWatch({ control: enrollmentForm.control, name: 'chargeDate' });
  const billFor: 'Prior' | 'Next' = useWatch({ control: enrollmentForm.control, name: 'billFor' }) || 'Next';
  const selectedChargeDate =
    chargeDateOptions[selectedBillingInterval]?.find((option) => option.value === chargeDate) || null;

  const currentParentEmail = useWatch({ control: enrollmentForm.control, name: 'parentEmail' });
  const subsidized = useWatch({ control: enrollmentForm.control, name: 'subsidized' });
  const subsidyCopayment = useWatch({ control: enrollmentForm.control, name: 'subsidyCopayment' });

  const planStartDate: string = useWatch({ control: enrollmentForm.control, name: 'planStartDate' }) || '';

  const setParentEmail = useCallback(
    (parent: any) => {
      enrollmentForm.setValue('parentEmail', currentParentEmail === parent.email ? '' : parent.email || '');
    },
    [currentParentEmail, enrollmentForm]
  );

  const canCalculateBilling = useMemo(() => {
    return chargeDate && selectedBillingInterval && planStartDate && billFor;
  }, [chargeDate, selectedBillingInterval, planStartDate, billFor]);

  const startBillingDate = useMemo(() => {
    if (canCalculateBilling) {
      return calculateStartBillingDate(selectedBillingInterval as BillingIntervalEnum, dayjs(planStartDate).toDate());
    } else {
      return null;
    }
  }, [canCalculateBilling, planStartDate, selectedBillingInterval]);

  const endBillingDate = useMemo(() => {
    if (canCalculateBilling) {
      return calculateEndBillingDate(
        chargeDate,
        selectedBillingInterval as BillingIntervalEnum,
        dayjs(planStartDate).toDate()
      );
    } else {
      return null;
    }
  }, [chargeDate, canCalculateBilling, planStartDate, selectedBillingInterval]);

  const endEnrollmentRange = useMemo(() => {
    if (billFor === 'Prior') {
      if (selectedBillingInterval === BillingIntervalEnum.WEEKLY) {
        return dayjs(startBillingDate).subtract(1, 'day').toDate();
      } else {
        return dayjs(endBillingDate).toDate();
      }
    }
    return endBillingDate;
  }, [billFor, endBillingDate, selectedBillingInterval, startBillingDate]);

  const weekdayNumbers = useMemo(() => convertScheduledDaysToWeekdayNumbers(scheduledDays) || [], [scheduledDays]);

  const shouldCalculateProrate = shouldCalculateProrateAmount(
    chargeDate || '',
    selectedBillingInterval as BillingIntervalEnum,
    dayjs(planStartDate).toDate()
  );

  const recurringFees = useWatch({ control: enrollmentForm.control, name: 'recurringFees' });
  const tuition = useWatch({ control: enrollmentForm.control, name: 'tuition' });

  const recurringInvoiceTotal = calculateRecurringInvoiceTotal(recurringFees, tuition);

  const calculatedProrateAmount = useMemo(() => {
    if (typeof fixedProrateAmount === 'number') {
      return fixedProrateAmount;
    }
    return shouldCalculateProrate
      ? calculateProrateAmount(
          weekdayNumbers,
          startBillingDate,
          endBillingDate,
          dayjs(planStartDate).toDate(),
          endEnrollmentRange,
          recurringInvoiceTotal || 0,
          selectedBillingInterval as BillingIntervalEnum
        )
      : 0;
  }, [
    shouldCalculateProrate,
    weekdayNumbers,
    startBillingDate,
    endBillingDate,
    planStartDate,
    endEnrollmentRange,
    recurringInvoiceTotal,
    fixedProrateAmount,
    selectedBillingInterval,
  ]);

  const saveProrateAmount = (amount) => {
    if (amount) {
      setFixedProrateAmount(parseFloat(amount));
    }
    setIsModalOpen(false);
  };

  const nextInvoices = getNextInvoices(
    dayjs(planStartDate).toDate(),
    recurringInvoiceTotal,
    selectedBillingInterval as BillingIntervalEnum,
    chargeDate,
    shouldCalculateProrate,
    calculatedProrateAmount,
    billFor
  );

  // Format and save tuition and fees data to enrollment form
  useEffect(() => {
    if (!selectedBillingInterval || !planStartDate) return;

    let tuition = {};

    if (!subsidized || (subsidized && subsidyCopayment)) {
      if (nextInvoices.length >= 2) {
        const dueDate = nextInvoices[1].dueDate;
        const proratedDueDate = nextInvoices[0].dueDate;
        const proratedAmount = Math.max(nextInvoices[0].amount, 0);
        const proratedPeriod =
          nextInvoices.length > 0
            ? selectedBillingInterval === BillingIntervalEnum.WEEKLY
              ? t('enrollments.weekOf', { week: dayjs(nextInvoices[0].serviceStart).format('MMMM D') })
              : calculatedProrateAmount > 0
                ? `${dayjs(nextInvoices[0].serviceStart).format('MMMM D')} - ${dayjs(nextInvoices[0].serviceEnd).format('MMMM D, YYYY')}`
                : t('enrollments.monthOf', { month: dayjs(nextInvoices[0].serviceStart).format('MMMM') })
            : '';
        const firstServiceStart = nextInvoices[1].serviceStart;
        const firstServiceEnd = nextInvoices[1].serviceEnd;
        tuition = {
          amount: convertToCents(recurringInvoiceTotal),
          billingInterval: selectedBillingInterval,
          billFor: billFor,
          dueDate,
          proratedAmount,
          proratedPeriod,
          proratedDueDate,
          firstServiceStart,
          firstServiceEnd,
          isProrated: calculatedProrateAmount > 0,
          detailedTuition: {
            tuition: tuition,
            fees: recurringFees,
            chargeDate: chargeDate,
            subsidized: subsidized,
            subsidyCopayment: subsidyCopayment,
          },
        };
      }
    } else {
      tuition = {
        detailedTuition: {
          subsidized: subsidized,
          subsidyCopayment: subsidyCopayment,
        },
      };
    }

    const fees =
      enrollmentForm.getValues('oneTimeFees')?.map((invoice: any) => {
        return { ...invoice, dueDate: new Date(invoice.dueDate) };
      }) || [];

    const tuitionAndFees = { tuition, fees };

    // Save the formatted data to the enrollment form
    enrollmentForm.setValue('tuitionAndFees', tuitionAndFees);
  }, [
    selectedBillingInterval,
    planStartDate,
    subsidized,
    subsidyCopayment,
    nextInvoices,
    calculatedProrateAmount,
    recurringInvoiceTotal,
    billFor,
    chargeDate,
    recurringFees,
    tuition,
    enrollmentForm,
    t,
  ]);

  return (
    <div className="mb-6 space-y-6 rounded-md border-2 border-gray-300 bg-gray-50 p-4">
      <PageHeader pageName={'Enrollment'} classes="enrollments-page" />
      <BusinessCoachCard text={t('enrollments.coachMessage')} />

      <div className="space-y-4">
        <h2 className="text-xl font-semibold">{t('common.primaryPayer')}</h2>
        <div className="text-base">{t('enrollments.primaryPayerDescription')}</div>
        {invitationRecipients.map((parent, index) => (
          <div key={index}>
            <label className="flex items-center space-x-2">
              <RadioButton
                disabled={!parent.email || invitationRecipients.length <= 1}
                checked={currentParentEmail ? currentParentEmail === parent.email : index === 0}
                className="form-checkbox"
                onChange={() => setParentEmail(parent)}
              />
              <div className="flex flex-col">
                <Label>{parent.displayName}</Label>
                <span className="text-xs">{parent.email}</span>
              </div>
            </label>
          </div>
        ))}
      </div>

      <Divider />

      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <h2 className="text-xl font-semibold">{t('enrollments.recurringInvoice')}</h2>
          <ButtonLink
            postIcon={<LinkOutIcon className="size-4" />}
            onClick={() => window.open(HELP_ARTICLE_URL, '_blank')}
          >
            {t('enrollments.viewArticle')}
          </ButtonLink>
        </div>
        <div className="text-base">{t('enrollments.recurringInvoiceInstructions')}</div>
        <div className="flex items-center space-x-2">
          <Switch
            checked={subsidized}
            onChange={(e) => enrollmentForm.setValue('subsidized', e.target.checked)}
            label={t('enrollments.receivingSubsidizedCare')}
            wrapperClasses="flex-row-reverse"
          />
        </div>

        {subsidized && (
          <div className="flex items-center space-x-2">
            <Switch
              checked={subsidyCopayment}
              onChange={(e) => enrollmentForm.setValue('subsidyCopayment', e.target.checked)}
              label={t('enrollments.paySubsidyCopayment')}
              wrapperClasses="flex-row-reverse"
            />
          </div>
        )}

        {(!subsidized || (subsidized && subsidyCopayment)) && (
          <div className="grid grid-cols-1 gap-4 pt-2 md:grid-cols-2">
            <div>
              <Controller
                name="planStartDate"
                control={enrollmentForm.control}
                rules={{
                  required: t('enrollments.invoicePlan.startDateRequired'),
                  validate: {
                    maxDate: (date) =>
                      dayjs(date).isBefore(dayjs(dayjs().add(2, 'year'))) || t('enrollments.errors.maxDate'),
                  },
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    required
                    value={field.value ?? ''}
                    label={t('invoicePlan.startDate')}
                    type="date"
                    inputType="date"
                    min={dayjs().format('YYYY-MM-DD')}
                    error={enrollmentForm.formState.errors.planStartDate?.message}
                  />
                )}
              />
            </div>
            <div>
              <Controller
                name="planEndDate"
                control={enrollmentForm.control}
                rules={{
                  validate: {
                    maxDate: (date) =>
                      dayjs(date).isBefore(dayjs(dayjs().add(2, 'year'))) || t('enrollments.errors.maxDate'),
                  },
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    value={field.value ?? ''}
                    label={t('invoicePlan.endDate')}
                    type="date"
                    inputType="date"
                    min={dayjs().format('YYYY-MM-DD')}
                    error={enrollmentForm.formState.errors.planEndDate?.message}
                  />
                )}
              />
            </div>
            <Select
              label={t('enrollments.enrollmentInfoBillingIntervalLabel')}
              options={billingIntervalOptions}
              required
              onChange={(option: Option) => {
                enrollmentForm.setValue('billingInterval', option.value);
                enrollmentForm.setValue('chargeDate', undefined);
              }}
              value={findSelectedOption(billingIntervalOptions, selectedBillingInterval)}
              error={enrollmentForm.formState.errors.billingInterval?.message}
            />

            <div className="flex flex-col gap-2">
              <Label required>{t('enrollments.billFor')}</Label>
              <div className="flex items-center">
                <Button
                  primary={!billFor || billFor === 'Prior'}
                  onClick={() => {
                    enrollmentForm.setValue('billFor', intervalBillingPeriodOptions[0].value as 'Prior' | 'Next');
                  }}
                  disabled={!selectedBillingInterval}
                  extraClasses="rounded-r-none"
                >
                  {intervalBillingPeriodOptions[0]?.label || 'Prior'}
                </Button>
                <Button
                  primary={billFor === 'Next'}
                  onClick={() => {
                    enrollmentForm.setValue('billFor', intervalBillingPeriodOptions[1].value as 'Prior' | 'Next');
                  }}
                  disabled={!selectedBillingInterval}
                  extraClasses="rounded-l-none"
                >
                  {intervalBillingPeriodOptions[1]?.label || 'Next'}
                </Button>
              </div>
              {enrollmentForm.formState.errors.billFor?.message && (
                <p className="text-sm text-red-600">{enrollmentForm.formState.errors.billFor.message}</p>
              )}
            </div>

            <div>
              {selectedBillingInterval === BillingIntervalEnum.BIWEEKLY ? (
                <Input
                  type="date"
                  inputType="date"
                  {...enrollmentForm.register('chargeDate')}
                  label={t('enrollments.dueDate')}
                  className="h-9 w-full rounded-md border p-2"
                  value={chargeDate}
                  disabled={!selectedBillingInterval}
                  required
                  min={dayjs.utc(planStartDate).format('YYYY-MM-DD')}
                  error={enrollmentForm.formState.errors.chargeDate?.message}
                />
              ) : (
                <Select
                  extraClasses="capitalize"
                  label={t('enrollments.dueDate')}
                  options={intervalChargeDatePeriodOptions}
                  required
                  onChange={(option: Option) => {
                    enrollmentForm.setValue('chargeDate', option.value);
                  }}
                  value={selectedChargeDate}
                  disabled={!selectedBillingInterval}
                  error={enrollmentForm.formState.errors.chargeDate?.message}
                />
              )}
              <div className="text-xs">{t('enrollments.chargeDateNote')}</div>
            </div>

            <div className="flex flex-col gap-2">
              <CurrencyInput
                label={t('enrollments.enrollmentInfoTuitionLabel')}
                onChange={({ target }) => {
                  const value = target.value.replace(',', '');
                  enrollmentForm.setValue('tuition', parseFloat(value));
                }}
                value={(() => {
                  const tuitionValue = tuition;
                  return tuitionValue && typeof tuitionValue === 'number' && tuitionValue > 0
                    ? tuitionValue.toString()
                    : '';
                })()}
                disabled={!selectedBillingInterval}
                required
                error={enrollmentForm.formState.errors.tuition?.message}
                postIcon={
                  selectedBillingInterval ? (
                    <span className="text-gray-800">/ {findSelectedOptionName(selectedBillingInterval)}</span>
                  ) : undefined
                }
              />
            </div>
          </div>
        )}
      </div>

      <Divider />

      {(!subsidized || (subsidized && subsidyCopayment)) && (
        <RecurringFeesForm
          control={enrollmentForm.control}
          setValue={enrollmentForm.setValue}
          clearErrors={enrollmentForm.clearErrors}
          feeCategoryOptions={feeCategoryOptions}
          setFeeCategoryOptions={setFeeCategoryOptions}
          watch={enrollmentForm.watch}
          selectedBillingInterval={selectedBillingInterval}
          findSelectedOptionName={findSelectedOptionName}
          register={enrollmentForm.register}
          errors={enrollmentForm.formState.errors}
          recurringInvoiceTotal={recurringInvoiceTotal}
          calculatedProrateAmount={calculatedProrateAmount}
          startDate={dayjs(planStartDate).toDate()}
          nextInvoices={nextInvoices}
          setIsModalOpen={setIsModalOpen}
        />
      )}

      <EnrollmentOneTimeInvoicesForm
        control={enrollmentForm.control}
        setValue={enrollmentForm.setValue}
        clearErrors={enrollmentForm.clearErrors}
        feeCategoryOptions={feeCategoryOptions}
        register={enrollmentForm.register}
        errors={enrollmentForm.formState.errors}
      />

      <EditProrateAmountModal
        amount={calculatedProrateAmount}
        billingInterval={selectedBillingInterval}
        enrollmentDate={dayjs.utc(planStartDate).format('MM/DD/YYYY')}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={saveProrateAmount}
      />
    </div>
  );
};

export default memo(StudentInvoice);
